import React from 'react'
import { useTranslation } from 'react-i18next'
import TableHeaders from '../../Interfaces/TableHeaders'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { CommunityFamiliesReportRow } from '../../../swagger'
import { Box } from '@mui/system'
import { TableHeaderForPDF } from '../../Table/TableHeaderForPDF'
import { styles } from './styles'
import { dateToDashString } from '../../../utils/dateUtility'

const widthForName = '10%'
const widthForEmail = '14%'
const widthForPhone = '10%'
const widthForStudent = '12%'
const widthForBirthday = '8%'
const widthForProgram = '10%'
const widthForStatus = '10%'
const widthForDate = '8%'

export const formatBirthday = (month?: number, year?: number): string => {
  if (month && year) {
    return `${month}/${year}`
  }
  return year ? `${year}` : ''
}

/**
 * Guards against empty dates.
 */
export const formatReportDate = (date?: Date): string => {
  if (!date || isNaN(new Date(date).getTime())) {
    return ''
  }
  return dateToDashString(date)
}

const FamiliesPDF: React.FC<{
  rows: CommunityFamiliesReportRow[]
  communityName?: string
}> = ({ rows, communityName }) => {
  const { t } = useTranslation()

  const tableHeaders: TableHeaders[] = [
    {
      label: t('FamiliesPDF.Table.Header.ParentFirstName', 'Parent First Name'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForName },
    },
    {
      label: t('FamiliesPDF.Table.Header.ParentLastName', 'Parent Last Name'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForName },
    },
    {
      label: t('FamiliesPDF.Table.Header.Email', 'Email'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForEmail },
    },
    {
      label: t('FamiliesPDF.Table.Header.Phone', 'Phone'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForPhone },
    },
    {
      label: t('FamiliesPDF.Table.Header.StudentName', 'Student Name'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForStudent },
    },
    {
      label: t('FamiliesPDF.Table.Header.StudentBirthday', 'Student Birthday'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForBirthday },
    },
    {
      label: t('FamiliesPDF.Table.Header.Program', 'Program'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForProgram },
    },
    {
      label: t(
        'FamiliesPDF.Table.Header.EnrollmentStatus',
        'Enrollment Status'
      ),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForStatus },
    },
    {
      label: t('FamiliesPDF.Table.Header.StatusDate', 'Status Date'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForDate },
    },
    {
      label: t('FamiliesPDF.Table.Header.EnrolledDate', 'Enrolled Date'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForDate },
    },
  ]

  return (
    <Box>
      <div style={styles.familyInfo}>
        <Typography
          style={styles.title}
          variant="subtitle1"
          component="h5"
          justifyContent={'flex-start'}
        >
          {communityName
            ? t(
                'FamiliesPDF.Section.FamiliesInformation',
                'Families - {{communityName}}',
                {
                  communityName,
                }
              )
            : t('FamiliesPDF.Section.Families', 'Families')}
        </Typography>

        <div style={styles.familyMetaRow}>
          {t('FamiliesPDF.Section.TotalEnrollments', 'Enrollments')}:{' '}
          {rows.length}
        </div>
      </div>
      <Box sx={styles.containerTable}>
        <Table aria-labelledby={'families-table-for-pdf'} style={styles.table}>
          <TableHead>
            <TableHeaderForPDF tableHeaders={tableHeaders} />
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForName }}
                >
                  {row.parentFirstName}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForName }}
                >
                  {row.parentLastName}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForEmail }}
                >
                  {row.parentEmail}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForPhone }}
                >
                  {row.parentPhone}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForStudent }}
                >
                  {row.studentName}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForBirthday }}
                >
                  {formatBirthday(row.studentBirthMonth, row.studentBirthYear)}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForProgram }}
                >
                  {row.programType}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForStatus }}
                >
                  {row.enrollmentStatus}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForDate }}
                >
                  {formatReportDate(row.enrollmentStatusDate)}
                </TableCell>
                <TableCell
                  style={{ ...styles.tableBodyCell, width: widthForDate }}
                >
                  {formatReportDate(row.enrolledDate)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  )
}

export default FamiliesPDF
